import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchProperties } from "../../../store/slices/propertySlice";
import { fetchUnits } from "../../../store/slices/unitSlice";
import { fetchStatisticsSummary } from "../../../store/slices/statisticsSlice";
import {
  createExpense,
  deleteExpense,
  fetchExpenses,
  updateExpense,
} from "../../../store/slices/expenseSlice";
import {
  createRecurringExpense,
  deleteRecurringExpense,
  fetchRecurringExpenses,
  updateRecurringExpense,
} from "../../../store/slices/recurringExpenseSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useTranslation } from "../../../translations/i18n";
import { CreateExpenseRequest } from "../../../models/RequestModels/CreateExpenseRequest";
import { UpdateExpenseRequest } from "../../../models/RequestModels/UpdateExpenseRequest";
import { CreateRecurringExpenseRequest } from "../../../models/RequestModels/CreateRecurringExpenseRequest";
import { UpdateRecurringExpenseRequest } from "../../../models/RequestModels/UpdateRecurringExpenseRequest";
import { ExpenseResponse } from "../../../models/ResponseModels/ExpenseResponse";
import { RecurringExpenseResponse } from "../../../models/ResponseModels/RecurringExpenseResponse";
import { StatisticsOverviewResponse } from "../../../models/ResponseModels/StatisticsSummaryResponse";

type GroupBy = "unit" | "property" | "month";
type PaymentStatus = "unpaid" | "deposit" | "paid" | null;

const today = () => new Date().toISOString().split("T")[0];

const addDays = (dateString: string, days: number) => {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const getStartOfMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-01`;
};

const getFallbackOverview = (currency: string): StatisticsOverviewResponse => ({
  currency,
  revenue: 0,
  manual_expenses: 0,
  recurring_expenses: 0,
  total_expenses: 0,
  net_profit: 0,
  booking_count: 0,
  expense_count: 0,
  booked_nights: 0,
  available_nights: 0,
  occupancy_percentage: 0,
  average_booking_value: 0,
  profit_margin_percentage: 0,
  expense_ratio_percentage: 0,
});

export const useStatisticsScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { account } = useAppSelector((state) => state.auth);
  const { properties, loading: propertiesLoading } = useAppSelector(
    (state) => state.properties,
  );
  const { units, loading: unitsLoading } = useAppSelector(
    (state) => state.units,
  );
  const {
    summary,
    loading: statisticsLoading,
    error: statisticsError,
  } = useAppSelector((state) => state.statistics);
  const {
    expenses,
    loading: expensesLoading,
    error: expensesError,
  } = useAppSelector((state) => state.expenses);
  const {
    recurringExpenses,
    loading: recurringExpensesLoading,
    error: recurringExpensesError,
  } = useAppSelector((state) => state.recurringExpenses);

  const defaultCurrency = account?.default_currency || "BAM";

  const [from, setFrom] = useState(getStartOfMonth());
  const [until, setUntil] = useState(today());
  const [days, setDays] = useState(30);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | "">("");
  const [selectedUnitId, setSelectedUnitId] = useState<number | "">("");
  const [groupBy, setGroupBy] = useState<GroupBy>("unit");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(null);

  const [isFilterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isExpenseActionDialogOpen, setExpenseActionDialogOpen] =
    useState(false);

  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [recurringExpenseModalOpen, setRecurringExpenseModalOpen] =
    useState(false);

  const [selectedExpense, setSelectedExpense] =
    useState<ExpenseResponse | null>(null);
  const [selectedRecurringExpense, setSelectedRecurringExpense] =
    useState<RecurringExpenseResponse | null>(null);

  const loading =
    propertiesLoading ||
    unitsLoading ||
    statisticsLoading ||
    expensesLoading ||
    recurringExpensesLoading;

  const error = statisticsError || expensesError || recurringExpensesError;

  const filteredUnits = useMemo(() => {
    if (!selectedPropertyId) {
      return units;
    }

    return units.filter((unit) => unit.property_id === selectedPropertyId);
  }, [units, selectedPropertyId]);

  const overview = summary?.overview || getFallbackOverview(defaultCurrency);
  const currencyOverviews = summary?.currency_overviews?.length
    ? summary.currency_overviews
    : [overview];

  const statisticsParams = useMemo(
    () => ({
      from,
      until,
      property_id: selectedPropertyId || undefined,
      unit_id: selectedUnitId || undefined,
      payment_status: paymentStatus || undefined,
      group_by: groupBy,
    }),
    [from, until, selectedPropertyId, selectedUnitId, paymentStatus, groupBy],
  );

  const expenseParams = useMemo(
    () => ({
      from,
      until,
      property_id: selectedPropertyId || undefined,
      unit_id: selectedUnitId || undefined,
    }),
    [from, until, selectedPropertyId, selectedUnitId],
  );

  const recurringExpenseParams = useMemo(
    () => ({
      property_id: selectedPropertyId || undefined,
      unit_id: selectedUnitId || undefined,
    }),
    [selectedPropertyId, selectedUnitId],
  );

  const refreshStatistics = useCallback(() => {
    dispatch(fetchStatisticsSummary(statisticsParams));
  }, [dispatch, statisticsParams]);

  const refreshExpenses = useCallback(() => {
    dispatch(fetchExpenses(expenseParams));
    dispatch(fetchRecurringExpenses(recurringExpenseParams));
  }, [dispatch, expenseParams, recurringExpenseParams]);

  const refreshAll = useCallback(() => {
    refreshStatistics();
    refreshExpenses();
  }, [refreshStatistics, refreshExpenses]);

  useEffect(() => {
    dispatch(fetchProperties());
    dispatch(fetchUnits());
  }, [dispatch]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const setDateRangeFromToday = (nextDays: number) => {
    const nextUntil = today();
    const nextFrom = addDays(nextUntil, -nextDays);

    setDays(nextDays);
    setFrom(nextFrom);
    setUntil(nextUntil);
  };

  const setThisMonth = () => {
    setDays(30);
    setFrom(getStartOfMonth());
    setUntil(today());
  };

  const handlePropertyChange = (value: number | "") => {
    setSelectedPropertyId(value);

    if (!selectedUnitId) {
      return;
    }

    const selectedUnit = units.find((unit) => unit.id === selectedUnitId);

    if (selectedUnit && value && selectedUnit.property_id !== value) {
      setSelectedUnitId("");
    }
  };

  const openExpenseModal = (expense?: ExpenseResponse) => {
    setSelectedExpense(expense || null);
    setExpenseActionDialogOpen(false);
    setExpenseModalOpen(true);
  };

  const closeExpenseModal = () => {
    setSelectedExpense(null);
    setExpenseModalOpen(false);
  };

  const openRecurringExpenseModal = (
    recurringExpense?: RecurringExpenseResponse,
  ) => {
    setSelectedRecurringExpense(recurringExpense || null);
    setExpenseActionDialogOpen(false);
    setRecurringExpenseModalOpen(true);
  };

  const closeRecurringExpenseModal = () => {
    setSelectedRecurringExpense(null);
    setRecurringExpenseModalOpen(false);
  };

  const submitExpense = async (
    body: CreateExpenseRequest | UpdateExpenseRequest,
  ) => {
    const result = selectedExpense
      ? await dispatch(
          updateExpense({
            id: selectedExpense.id,
            body: body as UpdateExpenseRequest,
          }),
        )
      : await dispatch(createExpense(body as CreateExpenseRequest));

    if (
      createExpense.fulfilled.match(result) ||
      updateExpense.fulfilled.match(result)
    ) {
      closeExpenseModal();
      refreshAll();
    }
  };

  const submitRecurringExpense = async (
    body: CreateRecurringExpenseRequest | UpdateRecurringExpenseRequest,
  ) => {
    const result = selectedRecurringExpense
      ? await dispatch(
          updateRecurringExpense({
            id: selectedRecurringExpense.id,
            body: body as UpdateRecurringExpenseRequest,
          }),
        )
      : await dispatch(
          createRecurringExpense(body as CreateRecurringExpenseRequest),
        );

    if (
      createRecurringExpense.fulfilled.match(result) ||
      updateRecurringExpense.fulfilled.match(result)
    ) {
      closeRecurringExpenseModal();
      refreshAll();
    }
  };

  const confirmDeleteExpense = async (expense: ExpenseResponse) => {
    if (!window.confirm(`Delete ${expense.name}?`)) {
      return;
    }

    const result = await dispatch(deleteExpense(expense.id));

    if (deleteExpense.fulfilled.match(result)) {
      refreshAll();
    }
  };

  const confirmDeleteRecurringExpense = async (
    recurringExpense: RecurringExpenseResponse,
  ) => {
    if (!window.confirm(`Delete ${recurringExpense.name}?`)) {
      return;
    }

    const result = await dispatch(deleteRecurringExpense(recurringExpense.id));

    if (deleteRecurringExpense.fulfilled.match(result)) {
      refreshAll();
    }
  };

  return {
    t,

    from,
    until,
    setFrom,
    setUntil,

    days,
    setDateRangeFromToday,
    setThisMonth,

    selectedPropertyId,
    setSelectedPropertyId: handlePropertyChange,

    selectedUnitId,
    setSelectedUnitId,

    groupBy,
    setGroupBy,

    paymentStatus,
    setPaymentStatus,

    properties,
    units,
    filteredUnits,

    summary,
    overview,
    currencyOverviews,
    expenses,
    recurringExpenses,

    loading,
    error,

    defaultCurrency,

    isFilterDialogOpen,
    setFilterDialogOpen,

    isExpenseActionDialogOpen,
    setExpenseActionDialogOpen,

    refreshAll,

    expenseModalOpen,
    selectedExpense,
    openExpenseModal,
    closeExpenseModal,
    submitExpense,
    confirmDeleteExpense,

    recurringExpenseModalOpen,
    selectedRecurringExpense,
    openRecurringExpenseModal,
    closeRecurringExpenseModal,
    submitRecurringExpense,
    confirmDeleteRecurringExpense,
  };
};
